import { ABTestPublishedStatus, SelectedStartType } from "../../config/enums";
import { FieldMinDate } from "../../config/fieldMinDates";
import { FieldValidator } from "../../config/fieldValidators";
import { LookupOptionFilter } from "../../config/lookupOptionFilters";
import { VisibilityRule } from "../../config/visibilityRules";

type Rec = Record<string, unknown>;

export const MinimumTestPeriodHours = 24;

const is = (value: unknown, expected: string | number) =>
  String(value ?? "") === String(expected);

const startOfDay = (value: unknown) => {
  const date = new Date(value as string);

  date.setHours(0, 0, 0, 0);
  return date;
};

// Dates are compared by day, and nothing set is later than anything.
const isSameOrLaterDate = (date: unknown, other: unknown) =>
  !date || !other || startOfDay(date) >= startOfDay(other);

export const isPublishedABTest = (record: Rec) =>
  is(record.statuscode, ABTestPublishedStatus.Published);

export const hasWinner = (record: Rec) =>
  is(record.g4c_winningcommunication, 0) || is(record.g4c_winningcommunication, 1);

export const abtestVisibilityRules: Record<string, VisibilityRule> = {
  abtestDraft: (record) => !isPublishedABTest(record),
  abtestPublished: isPublishedABTest,
  abtestSaved: (record) => Boolean(record.id),
  abtestAudienceSummary: (record) =>
    !isPublishedABTest(record) && Boolean(record.g4c_audienceid),
  abtestStartDate: (record) =>
    is(record.g4c_starttype, SelectedStartType.Scheduled) ||
    (isPublishedABTest(record) && Boolean(record.g4c_startdate)),
};

/** The test can be edited and saved only until it is published. */
export const abtestEditableRule = "abtestDraft";

const laterThanStart = (date: unknown, record: Rec) =>
  is(record.g4c_starttype, SelectedStartType.Scheduled) && record.g4c_startdate
    ? isSameOrLaterDate(date, record.g4c_startdate)
    : is(record.g4c_starttype, SelectedStartType.Immediately)
    ? isSameOrLaterDate(date, new Date())
    : true;

const required = (value: unknown) =>
  value === null || value === undefined || value === "" ? "This field is required" : "";

export const abtestValidators: Record<string, FieldValidator> = {
  // A searchable lookup has no native control for the browser's required check to read.
  abtestRequiredLookup: required,
  abtestTestPercentage: (value) =>
    Number(value) > 0 && Number(value) <= 100
      ? ""
      : "You must enter a number between 1 and 100",
  abtestTestPeriod: (value) =>
    Number(value) >= MinimumTestPeriodHours
      ? ""
      : `Test Period must be at least ${MinimumTestPeriodHours} hours`,
  abtestEndDate: (date, record) =>
    record.g4c_enddate && !laterThanStart(date, record) ? "Select a later date" : "",
};

/** The end date can't be before the start date, or before today when started immediately. */
export const abtestMinDates: Record<string, FieldMinDate> = {
  abtestEndDate: (record) =>
    is(record.g4c_starttype, SelectedStartType.Scheduled) && record.g4c_startdate
      ? new Date(record.g4c_startdate as string)
      : is(record.g4c_starttype, SelectedStartType.Immediately)
      ? new Date()
      : undefined,
};

const withoutOther = (other: unknown): LookupOptionFilter => (options) =>
  other ? options.filter((option) => !is(option.Key, other as string)) : options;

/** The two communications of a test have to differ, so each lookup leaves out the other's choice. */
export const abtestOptionFilters: Record<string, LookupOptionFilter> = {
  abtestCommunicationA: (options, record) =>
    withoutOther(record.g4c_communicationbid)(options, record),
  abtestCommunicationB: (options, record) =>
    withoutOther(record.g4c_communicationaid)(options, record),
};
